import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Check, X, ArrowRight, ArrowLeft, User, Camera, Sun, Moon, Mail, Music, Search, ExternalLink, BadgeCheck } from 'lucide-react';
import { brandAssets } from '../../data/mock';
import debounce from '../../utils/debounce';
import ImageCropper from '../../components/shared/ImageCropper';

const OnboardingFlow = ({ onComplete }) => {
  const navigate = useNavigate();
  const { user, checkUsername, updateOnboarding, completeOnboarding, api } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [step, setStep] = useState(1); // 1: Username, 2: Profile, 3: Email
  const [formData, setFormData] = useState({
    username: '',
    displayName: user?.display_name || '',
    bio: '',
    email: user?.email || '',
  });
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Profile image state
  const [profileImage, setProfileImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const fileInputRef = useRef(null);

  // Artist verification state
  const [showArtistFlow, setShowArtistFlow] = useState(false);
  const [artistStep, setArtistStep] = useState(1); // 1: search, 2: social link, 3: confirm
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistSearchResults, setArtistSearchResults] = useState([]);
  const [artistSearching, setArtistSearching] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [socialLink, setSocialLink] = useState('');
  const [artistSubmitting, setArtistSubmitting] = useState(false);
  const [artistClaimed, setArtistClaimed] = useState(false);

  const totalSteps = 3;

  // Debounced username check
  const debouncedCheckUsername = useCallback(
    debounce(async (username) => {
      if (username.length < 3) {
        setUsernameStatus({ checking: false, available: null, message: 'Username must be at least 3 characters' });
        return;
      }

      if (username.length > 15) {
        setUsernameStatus({ checking: false, available: false, message: 'Username must be 15 characters or less' });
        return;
      }

      if (!/^[a-zA-Z0-9._]+$/.test(username)) {
        setUsernameStatus({ checking: false, available: false, message: 'Only letters, numbers, periods, and underscores allowed' });
        return;
      }

      // Username can't start or end with period/underscore
      if (/^[._]|[._]$/.test(username)) {
        setUsernameStatus({ checking: false, available: false, message: 'Username cannot start or end with . or _' });
        return;
      }

      setUsernameStatus({ checking: true, available: null, message: '' });

      try {
        const result = await checkUsername(username);
        setUsernameStatus({
          checking: false,
          available: result.available,
          message: result.available ? 'Username is available!' : 'Sorry, this username is taken! Try something else.'
        });
      } catch (err) {
        setUsernameStatus({ checking: false, available: null, message: 'Error checking username' });
      }
    }, 500),
    [checkUsername]
  );

  useEffect(() => {
    if (formData.username) {
      debouncedCheckUsername(formData.username);
    } else {
      setUsernameStatus({ checking: false, available: null, message: '' });
    }
  }, [formData.username, debouncedCheckUsername]);

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!usernameStatus.available) return;

    setLoading(true);
    setError('');

    try {
      await updateOnboarding({ username: formData.username.toLowerCase() });
      setStep(2);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail[0]?.msg || 'Validation error');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to set username');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle profile image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageToCrop(event.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle cropped image
  const handleCropComplete = (croppedImage) => {
    setProfileImage(croppedImage);
    setShowCropper(false);
    setImageToCrop(null);
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData = {
        display_name: formData.displayName,
        bio: formData.bio,
      };
      
      // Include profile image if uploaded
      if (profileImage) {
        updateData.profile_image = profileImage;
      }
      
      await updateOnboarding(updateData);
      setStep(3); // Go to email step
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail[0]?.msg || 'Validation error');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to save profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Update email
      if (formData.email) {
        await api.put('/api/users/email', { email: formData.email });
      }
      await completeOnboarding();
      // Set flag to show Apple Music modal in the dashboard
      localStorage.setItem('tapInShowAppleMusicModal', 'true');
      // Clear any previous modal shown state
      sessionStorage.removeItem('tapInAppleMusicModalShown');
      console.log('[Onboarding] Set flag to show Apple Music modal');
      onComplete?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to save email');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkipEmail = async () => {
    setLoading(true);
    try {
      await completeOnboarding();
      // Set flag to show Apple Music modal in the dashboard
      localStorage.setItem('tapInShowAppleMusicModal', 'true');
      // Clear any previous modal shown state
      sessionStorage.removeItem('tapInAppleMusicModalShown');
      console.log('[Onboarding] Set flag to show Apple Music modal (skipped email)');
      onComplete?.();
    } catch (err) {
      setError('Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  // Apple Music artist search
  const searchArtists = async (query) => {
    if (!query || query.length < 2) {
      setArtistSearchResults([]);
      return;
    }
    setArtistSearching(true);
    try {
      // Use Apple Music API via MusicKit or catalog search
      const musicInstance = window.MusicKit?.getInstance?.();
      if (musicInstance) {
        const results = await musicInstance.api.music(`/v1/catalog/us/search`, { term: query, types: 'artists', limit: 8 });
        const artists = results?.data?.results?.artists?.data || [];
        setArtistSearchResults(artists.map(a => ({
          id: a.id,
          name: a.attributes?.name || '',
          image: a.attributes?.artwork?.url?.replace('{w}', '120').replace('{h}', '120') || '',
          genre: a.attributes?.genreNames?.[0] || '',
          url: a.attributes?.url || ''
        })));
      } else {
        // Fallback: search via backend proxy or just show the typed name
        setArtistSearchResults([{
          id: `manual-${Date.now()}`,
          name: query,
          image: '',
          genre: '',
          url: ''
        }]);
      }
    } catch (err) {
      console.error('Artist search error:', err);
      // Fallback 
      setArtistSearchResults([{
        id: `manual-${Date.now()}`,
        name: query,
        image: '',
        genre: '',
        url: ''
      }]);
    } finally {
      setArtistSearching(false);
    }
  };

  // Submit artist verification
  const submitArtistClaim = async () => {
    if (!selectedArtist || !user?.id) return;
    setArtistSubmitting(true);
    try {
      const params = new URLSearchParams({
        user_id: user.id,
        username: formData.username,
        claimed_artist_name: selectedArtist.name,
        apple_music_artist_id: selectedArtist.id || '',
        apple_music_artist_image: selectedArtist.image || '',
        social_link: socialLink
      });
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/artist-verification/submit?${params}`, {
        method: 'POST'
      });
      setArtistClaimed(true);
      setArtistStep(3);
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setArtistSubmitting(false);
    }
  };

  // Debounced artist search
  const debouncedArtistSearch = useCallback(
    debounce((q) => searchArtists(q), 400),
    []
  );

  return (
    <div className={`min-h-screen flex flex-col relative overflow-hidden ${isDarkMode ? 'bg-[#0a0a0f]' : 'bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100'}`} data-testid="onboarding-flow">
      
      {/* Animated Gradient Background with Floating Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Mesh */}
        <div 
          className={`absolute inset-0 ${isDarkMode ? 'opacity-30' : 'opacity-40'}`}
          style={{
            background: isDarkMode 
              ? 'radial-gradient(ellipse at 20% 20%, rgba(139, 92, 246, 0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)'
              : 'radial-gradient(ellipse at 20% 20%, rgba(139, 92, 246, 0.2) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(59, 130, 246, 0.2) 0%, transparent 50%)'
          }}
        />
        
        {/* Floating Orb 1 - Purple (top right) */}
        <div 
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%)',
            animationDuration: '8s'
          }}
        />
        
        {/* Floating Orb 2 - Blue (bottom left) */}
        <div 
          className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full blur-3xl animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
            animationDuration: '10s',
            animationDelay: '2s'
          }}
        />
        
        {/* Floating Orb 3 - Indigo (center) */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl animate-pulse opacity-50"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, transparent 70%)',
            animationDuration: '12s',
            animationDelay: '4s'
          }}
        />
        
        {/* Noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
          }}
        />
      </div>

      {/* Header - Glass Style */}
      <div className="relative z-10 p-6">
        {/* Top row with back button and dark mode */}
        <div className="flex items-center justify-between mb-4">
          {/* Back button - Glass style */}
          <button
            onClick={() => {
              if (step > 1) {
                setStep(step - 1);
              } else {
                navigate('/');
              }
            }}
            className={`w-11 h-11 rounded-2xl flex items-center justify-center touch-manipulation transition-all duration-200 ${
              isDarkMode 
                ? 'bg-white/10 backdrop-blur-xl border border-white/10 text-white hover:bg-white/15' 
                : 'bg-white/70 backdrop-blur-xl border border-white/50 text-gray-700 shadow-lg shadow-purple-500/5 hover:bg-white/90'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            data-testid="onboarding-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {/* Centered Logo with glow */}
          <div className="relative">
            <div className={`absolute inset-0 blur-xl rounded-full ${isDarkMode ? 'bg-purple-500/30' : 'bg-purple-400/20'}`} />
            <img 
              src={brandAssets.logo} 
              alt="Tap In" 
              className="w-12 h-12 object-contain relative z-10 drop-shadow-lg"
            />
          </div>

          {/* Dark Mode Toggle - Glass style */}
          <button
            onClick={toggleDarkMode}
            className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-200 ${
              isDarkMode 
                ? 'bg-white/10 backdrop-blur-xl border border-white/10 text-yellow-400 hover:bg-white/15' 
                : 'bg-white/70 backdrop-blur-xl border border-white/50 text-gray-600 shadow-lg shadow-purple-500/5 hover:bg-white/90'
            }`}
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
        
        {/* Progress Bar - Glass style */}
        <div className={`flex gap-2 p-1 rounded-full ${isDarkMode ? 'bg-white/5' : 'bg-white/50 backdrop-blur-sm'}`}>
          {[...Array(totalSteps)].map((_, i) => (
            <div 
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                step > i 
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 shadow-sm shadow-purple-500/50' 
                  : (isDarkMode ? 'bg-white/10' : 'bg-gray-200/50')
              }`} 
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-5 pb-12">
        <div className="max-w-sm mx-auto w-full">
          
          {/* Step 1: Username */}
          {step === 1 && (
            <div 
              className={`rounded-3xl p-6 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-white/5 backdrop-blur-2xl border border-white/10' 
                  : 'bg-white/70 backdrop-blur-2xl border border-white/50'
              }`}
              style={{
                boxShadow: isDarkMode 
                  ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                  : '0 4px 6px rgba(0, 0, 0, 0.02), 0 10px 20px rgba(0, 0, 0, 0.04), 0 25px 50px rgba(139, 92, 246, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
              }}
            >
              <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Choose a username
              </h1>
              <p className={`mb-6 text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
                This is how others will find you
              </p>

              <form onSubmit={handleUsernameSubmit} className="space-y-4">
                <div>
                  <div className="relative">
                    <span 
                      className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-xl"
                      style={{ color: isDarkMode ? '#c084fc' : '#000000' }}
                    >@</span>
                    <input
                      type="text"
                      placeholder="username"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, '') })}
                      maxLength={15}
                      className={`w-full pl-11 pr-12 py-4 rounded-2xl border-2 outline-none transition-all duration-200 text-lg ${
                        isDarkMode 
                          ? `bg-white/5 backdrop-blur-sm text-white placeholder-white/30 ${
                              usernameStatus.available === true ? 'border-green-500/50 shadow-[0_0_20px_rgba(34,197,94,0.15)]' :
                              usernameStatus.available === false ? 'border-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.15)]' :
                              'border-white/10 focus:border-purple-500/50 focus:shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                            }`
                          : `bg-white/80 backdrop-blur-sm text-gray-900 placeholder-gray-400 ${
                              usernameStatus.available === true ? 'border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.15)]' :
                              usernameStatus.available === false ? 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.15)]' :
                              'border-gray-200 focus:border-purple-500 focus:shadow-[0_0_20px_rgba(139,92,246,0.15)]'
                            }`
                      }`}
                      data-testid="username-input"
                    />
                    {/* Status Icon */}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      {usernameStatus.checking && (
                        <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                      )}
                      {!usernameStatus.checking && usernameStatus.available === true && (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/30">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {!usernameStatus.checking && usernameStatus.available === false && (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/30">
                          <X className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {usernameStatus.message && (
                    <p className={`text-sm mt-2 ${usernameStatus.available ? 'text-green-500' : 'text-red-400'}`}>
                      {usernameStatus.message}
                    </p>
                  )}
                </div>

                {error && <p className="text-red-400 text-sm text-center">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || !usernameStatus.available}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-40 touch-manipulation transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25 active:scale-[0.98]"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  data-testid="username-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Continue <ArrowRight className="w-5 h-5" /></>
                  )}
                </button>
              </form>

              {/* I am an Artist button */}
              {!artistClaimed && (
                <button
                  onClick={() => { setShowArtistFlow(true); setArtistStep(1); }}
                  className={`w-full mt-3 py-3 rounded-2xl font-medium flex items-center justify-center gap-2 transition-all duration-200 border-2 ${
                    isDarkMode
                      ? 'border-purple-500/30 text-purple-300 bg-purple-500/10 hover:bg-purple-500/20'
                      : 'border-purple-400 text-purple-600 bg-purple-50 hover:bg-purple-100'
                  }`}
                  data-testid="i-am-artist-btn"
                >
                  I am an Artist
                </button>
              )}

              {/* Artist claimed confirmation inline */}
              {artistClaimed && (
                <div className={`mt-3 py-3 px-4 rounded-2xl flex items-center gap-2 ${
                  isDarkMode ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'
                }`}>
                  <BadgeCheck className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                    Artist verification submitted for review
                  </span>
                </div>
              )}
            </div>
          )}

      {/* Artist Verification Flow Modal */}
      {showArtistFlow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-3xl p-6 max-h-[85vh] overflow-y-auto ${
            isDarkMode 
              ? 'bg-gray-900 border border-gray-700' 
              : 'bg-white border border-gray-200 shadow-2xl'
          }`}>

            {/* Step 1: Search for your artist profile */}
            {artistStep === 1 && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3 ${
                    isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                  }`}>
                    <Music className={`w-7 h-7 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Find your artist profile
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Search Apple Music for your artist name
                  </p>
                </div>

                {/* Search Input */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    placeholder="Search your artist name..."
                    value={artistSearchQuery}
                    onChange={(e) => {
                      setArtistSearchQuery(e.target.value);
                      debouncedArtistSearch(e.target.value);
                    }}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border outline-none ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    data-testid="artist-search-input"
                    autoFocus
                  />
                  {artistSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                {/* Search Results */}
                {artistSearchResults.length > 0 && (
                  <div className={`rounded-xl overflow-hidden divide-y max-h-60 overflow-y-auto ${
                    isDarkMode ? 'divide-gray-700 border border-gray-700' : 'divide-gray-100 border border-gray-200'
                  }`}>
                    {artistSearchResults.map((artist) => (
                      <button
                        key={artist.id}
                        onClick={() => { setSelectedArtist(artist); setArtistStep(2); }}
                        className={`w-full flex items-center gap-3 p-3 transition-colors ${
                          isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-purple-50'
                        }`}
                        data-testid={`artist-result-${artist.id}`}
                      >
                        {artist.image ? (
                          <img src={artist.image} alt={artist.name} className="w-12 h-12 rounded-lg object-cover" />
                        ) : (
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                          }`}>
                            <Music className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                          </div>
                        )}
                        <div className="text-left flex-1 min-w-0">
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{artist.name}</p>
                          {artist.genre && (
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{artist.genre}</p>
                          )}
                        </div>
                        <ArrowRight className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      </button>
                    ))}
                  </div>
                )}

                {/* Cancel */}
                <button
                  onClick={() => { setShowArtistFlow(false); setArtistSearchQuery(''); setArtistSearchResults([]); }}
                  className={`w-full py-3 rounded-xl font-medium ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Step 2: Social link + confirmation */}
            {artistStep === 2 && selectedArtist && (
              <div className="space-y-4">
                <div className="text-center">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Verify you're {selectedArtist.name}
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Add a social media link so we can confirm your identity
                  </p>
                </div>

                {/* Selected Artist Card */}
                <div className={`flex items-center gap-3 p-4 rounded-xl ${
                  isDarkMode ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'
                }`}>
                  {selectedArtist.image ? (
                    <img src={selectedArtist.image} alt={selectedArtist.name} className="w-14 h-14 rounded-xl object-cover" />
                  ) : (
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                    }`}>
                      <Music className={`w-7 h-7 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedArtist.name}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>Apple Music Artist</p>
                  </div>
                  <BadgeCheck className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                </div>

                {/* Username Match Indicator */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  formData.username.toLowerCase() === selectedArtist.name.toLowerCase().replace(/\s+/g, '')
                    ? (isDarkMode ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600')
                    : (isDarkMode ? 'bg-yellow-500/10 text-yellow-400' : 'bg-yellow-50 text-yellow-600')
                }`}>
                  {formData.username.toLowerCase() === selectedArtist.name.toLowerCase().replace(/\s+/g, '') ? (
                    <><Check className="w-4 h-4" /> Username matches artist name</>
                  ) : (
                    <><User className="w-4 h-4" /> Username: @{formData.username} | Artist: {selectedArtist.name}</>
                  )}
                </div>

                {/* Social Link */}
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Social media link (Instagram, Twitter/X, TikTok)
                  </label>
                  <div className="relative">
                    <ExternalLink className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      type="url"
                      placeholder="https://instagram.com/yourprofile"
                      value={socialLink}
                      onChange={(e) => setSocialLink(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border outline-none ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                      data-testid="artist-social-input"
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  onClick={submitArtistClaim}
                  disabled={artistSubmitting || !socialLink}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
                  data-testid="artist-submit-btn"
                >
                  {artistSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Submit for Verification</>
                  )}
                </button>

                {/* Back */}
                <button
                  onClick={() => { setArtistStep(1); setSelectedArtist(null); }}
                  className={`w-full py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Back to search
                </button>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {artistStep === 3 && (
              <div className="text-center space-y-4 py-4">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
                }`}>
                  <Check className={`w-8 h-8 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                </div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Submission Received
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Your artist verification request for <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>"{selectedArtist?.name}"</strong> has been successfully submitted and will be reviewed by the Tap In team within 24 hours. You'll be notified via your social media once approved.
                </p>
                <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Enjoy Tap In!
                </p>
                <button
                  onClick={() => { setShowArtistFlow(false); }}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold"
                >
                  Continue with Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      )}

          {/* Step 2: Profile */}
          {step === 2 && (
            <div 
              className={`rounded-3xl p-6 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-white/5 backdrop-blur-2xl border border-white/10' 
                  : 'bg-white/70 backdrop-blur-2xl border border-white/50'
              }`}
              style={{
                boxShadow: isDarkMode 
                  ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                  : '0 4px 6px rgba(0, 0, 0, 0.02), 0 10px 20px rgba(0, 0, 0, 0.04), 0 25px 50px rgba(139, 92, 246, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
              }}
            >
              <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Set up your profile
              </h1>
              <p className={`mb-6 text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
                Tell others a bit about yourself
              </p>

              <form onSubmit={handleProfileSubmit} className="space-y-5">
                {/* Profile Picture - Glass style */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      data-testid="profile-image-input"
                    />
                    
                    {/* Glow ring behind profile */}
                    <div 
                      className="absolute inset-0 -m-2 rounded-full blur-xl opacity-50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5) 0%, rgba(59, 130, 246, 0.5) 100%)'
                      }}
                    />
                    
                    {/* Profile image or placeholder */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="relative w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center transition-transform hover:scale-105 active:scale-95 touch-manipulation"
                      style={{
                        boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)'
                      }}
                      data-testid="profile-image-button"
                    >
                      {profileImage ? (
                        <img 
                          src={profileImage} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-14 h-14 text-white/80" />
                      )}
                    </button>
                    
                    {/* Camera button - Glass style */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`absolute -bottom-1 -right-1 w-10 h-10 rounded-full flex items-center justify-center transition-colors active:scale-95 touch-manipulation ${
                        isDarkMode 
                          ? 'bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20' 
                          : 'bg-white backdrop-blur-xl border-2 border-purple-500 text-purple-500 shadow-lg hover:bg-purple-50'
                      }`}
                      data-testid="camera-button"
                    >
                      <Camera className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Tap to add photo hint */}
                <p className={`text-center text-xs -mt-2 mb-2 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                  Tap to add a photo
                </p>

                {/* Display Name - Glass input (Required) */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    required
                    className={`w-full px-4 py-4 rounded-2xl border-2 outline-none transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-white/5 backdrop-blur-sm border-white/10 text-white placeholder-white/30 focus:border-purple-500/50 focus:shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                        : 'bg-white/80 backdrop-blur-sm border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:shadow-[0_0_20px_rgba(139,92,246,0.15)]'
                    }`}
                    data-testid="display-name-input"
                  />
                </div>

                {/* Bio - Glass textarea */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                    Bio <span className={isDarkMode ? 'text-white/40' : 'text-gray-400'}>(optional)</span>
                  </label>
                  <textarea
                    placeholder="Tell us about yourself..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={3}
                    className={`w-full px-4 py-4 rounded-2xl border-2 outline-none transition-all duration-200 resize-none ${
                      isDarkMode 
                        ? 'bg-white/5 backdrop-blur-sm border-white/10 text-white placeholder-white/30 focus:border-purple-500/50 focus:shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                        : 'bg-white/80 backdrop-blur-sm border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:shadow-[0_0_20px_rgba(139,92,246,0.15)]'
                    }`}
                    data-testid="bio-input"
                  />
                </div>

                {error && <p className="text-red-400 text-sm text-center">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || !formData.displayName.trim()}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] disabled:opacity-40 hover:shadow-lg hover:shadow-purple-500/25"
                  data-testid="profile-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Continue <ArrowRight className="w-5 h-5" /></>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 3: Email (Last step) */}
          {step === 3 && (
            <div 
              className={`rounded-3xl p-6 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-white/5 backdrop-blur-2xl border border-white/10' 
                  : 'bg-white/70 backdrop-blur-2xl border border-white/50'
              }`}
              style={{
                boxShadow: isDarkMode 
                  ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                  : '0 4px 6px rgba(0, 0, 0, 0.02), 0 10px 20px rgba(0, 0, 0, 0.04), 0 25px 50px rgba(139, 92, 246, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
              }}
            >
              <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Add your email
              </h1>
              <p className={`mb-6 text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
                For account recovery and updates
              </p>

              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                    Email Address
                  </label>
                  <div className="relative">
                    <div 
                      className="absolute left-4 top-1/2 -translate-y-1/2"
                      style={{ color: isDarkMode ? '#c084fc' : '#000000' }}
                    >
                      <Mail className="w-6 h-6" />
                    </div>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full pl-14 pr-4 py-4 rounded-2xl border-2 outline-none transition-all duration-200 ${
                        isDarkMode 
                          ? 'bg-white/5 backdrop-blur-sm border-white/10 text-white placeholder-white/30 focus:border-purple-500/50 focus:shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                          : 'bg-white/80 backdrop-blur-sm border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:shadow-[0_0_20px_rgba(139,92,246,0.15)]'
                      }`}
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {error && <p className="text-red-400 text-sm text-center">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] disabled:opacity-40 hover:shadow-lg hover:shadow-purple-500/25"
                  data-testid="email-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Get Started <ArrowRight className="w-5 h-5" /></>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleSkipEmail}
                  disabled={loading}
                  className={`w-full text-center py-2 font-medium transition-colors ${isDarkMode ? 'text-white/50 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="skip-email"
                >
                  Skip for now
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      
      {/* Image Cropper Modal */}
      <ImageCropper
        isOpen={showCropper}
        onClose={handleCropCancel}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        circularCrop={true}
        title="Crop Profile Photo"
      />
    </div>
  );
};

export default OnboardingFlow;
